import { cn } from '@/lib/utils'
import { CSSProperties } from 'react'
import BannerImage from 'app/frontend/base/components/BannerImage'
import Card from 'app/frontend/base/components/Card'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { usePageInfo } from 'app/frontend/base/config/queries'
import EntityLinksTile from './EntityLinksTile'
import Subheader from 'app/frontend/base/components/Subheader'
import { useTeamMenuItems } from '../../TeamDashboard/components/TeamSubheader'
import { useHasSidebar } from 'app/frontend/base/hooks/useHasSidebar'
import { TextStyle } from 'app/frontend/types/custom/CustomDomainHeaderConfig'
import { useCustomDomain } from 'app/frontend/base/hooks/useCustomDomain'

const styleConfig: Record<
  Exclude<TextStyle, 'cards'> | 'noImage',
  { title: string; subtitle: string; style?: CSSProperties }
> = {
  textShadow: {
    title:
      'text-white drop-shadow-[0_0_8px_rgba(0,0,0,0.8)] drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]',
    subtitle:
      'text-white/90 drop-shadow-[0_0_12px_rgba(0,0,0,1)] drop-shadow-[0_0_4px_rgba(0,0,0,1)] drop-shadow-[0_1px_2px_rgba(0,0,0,1)]',
  },
  scrim: {
    title: 'text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]',
    subtitle: 'text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]',
  },
  glass: {
    title: 'text-white backdrop-blur-xs bg-black/20 rounded-lg py-2 px-3',
    subtitle: 'text-white/80 backdrop-blur-xs bg-black/20 rounded-lg py-2 px-3',
  },
  outline: {
    title: 'text-white',
    subtitle: 'text-white/90 tracking-wide',
    style: {
      WebkitTextStroke: '4px rgba(0, 0, 0, 0.6)',
      paintOrder: 'stroke fill',
    },
  },
  noImage: {
    title: '',
    subtitle: '',
  },
}

const CustomTeamHeader = () => {
  const { guild, currentTeam: team } = useGuild()
  const { customDomain } = useCustomDomain()
  const {
    data: { gameVersion },
  } = usePageInfo()

  const { eventsMenuItems, lootMenuItems } = useTeamMenuItems()
  const hasSidebar = useHasSidebar()

  const uriLinkFragment = `${guild?.realm?.region.toLowerCase()}/${
    guild?.realm?.slug
  }/${guild?.slug}`

  const textStyle = customDomain?.headerConfig?.noImage
    ? 'noImage'
    : (customDomain?.headerConfig?.textStyle as TextStyle) ?? 'textShadow'

  const cardClassName = cn(
    customDomain?.headerConfig?.transparentCards
      ? 'bg-transparent border-none hover:bg-transparent'
      : '',
  )

  const contentOptions: Record<string, string> = {
    guildName: guild?.name ?? '',
    teamDescription: team?.description ?? '',
    teamName: team?.name ?? '',
    none: '',
  }

  const title =
    contentOptions[customDomain?.headerConfig?.titleType ?? 'guildName']

  const subtitle =
    contentOptions[
      customDomain?.headerConfig?.subtitleType ?? 'teamDescription'
    ]

  return (
    <>
      <header
        className={cn(
          '-mt-8 border-b-2 relative bg-cover bg-center',
          customDomain?.headerConfig?.noImage ? 'bg-background' : '',
        )}
        style={{
          minHeight: `${customDomain?.headerConfig?.height ?? 160}px`,
          backgroundImage: customDomain?.headerConfig?.noImage
            ? undefined
            : `url(${
                customDomain?.headerConfig?.backgroundImage ||
                `https://data.wowaudit.com/img/banners/${gameVersion}.jpg`
              })`,
        }}
      >
        {textStyle === 'scrim' && (
          <div className='absolute inset-0 bg-linear-to-r from-black/70 via-black/30 to-transparent' />
        )}

        <div
          className={cn(
            'relative z-10 flex justify-between items-center min-h-40 root-width py-4 gap-2 lg:gap-24 max-sm:flex-col max-sm:items-start',
            hasSidebar ? 'mx-0' : '',
          )}
        >
          <div className='flex items-center gap-2'>
            {customDomain?.headerConfig?.hideBanner ? null : (
              <BannerImage
                className='h-32 aspect-square max-sm:hidden'
                faction={guild?.faction}
                fileName={guild?.bannerFilename}
                size='custom'
              />
            )}

            <div className='flex flex-col gap-2 items-start'>
              {textStyle === 'cards' ? (
                <CardsText
                  title={title}
                  subtitle={subtitle}
                  cardClassName={cardClassName}
                />
              ) : (
                <StyledText
                  title={title}
                  subtitle={subtitle}
                  config={styleConfig[textStyle]}
                />
              )}
            </div>
          </div>

          {customDomain?.headerConfig?.hideLinks ? null : (
            <EntityLinksTile
              className={cardClassName}
              uriLinkFragment={uriLinkFragment}
            />
          )}
        </div>
      </header>

      {window.location.pathname.startsWith('/loot') ? (
        <Subheader menuItems={lootMenuItems} fullWidth={true} />
      ) : window.location.pathname.startsWith('/events') ||
        window.location.pathname.startsWith('/plans') ? (
        <Subheader menuItems={eventsMenuItems} fullWidth={true} />
      ) : null}
    </>
  )
}

const CardsText = ({
  title,
  subtitle,
  cardClassName,
}: {
  title: string
  subtitle: string
  cardClassName: string
}) => {
  return (
    <>
      {title ? (
        <Card className={cn(cardClassName, 'py-2 px-3')}>
          <h1 className='max-sm:text-xl leading-none'>{title}</h1>
        </Card>
      ) : null}

      {subtitle ? (
        <Card className={cn(cardClassName, 'py-2 px-3')}>
          <p className={cardClassName}>{subtitle}</p>
        </Card>
      ) : null}
    </>
  )
}

const StyledText = ({
  title,
  subtitle,
  config,
}: {
  title: string
  subtitle: string
  config: typeof styleConfig[keyof typeof styleConfig]
}) => (
  <>
    {title ? (
      <h1
        className={cn('max-sm:text-xl leading-none', config.title)}
        style={config.style}
      >
        {title}
      </h1>
    ) : null}

    {subtitle ? (
      <p className={config.subtitle} style={config.style}>
        {subtitle}
      </p>
    ) : null}
  </>
)

export default CustomTeamHeader
